import { Controller } from '@hotwired/stimulus';

import {
  currentSection,
  docBodyRequiresAnswerCommentInDom,
  getCsrfToken,
  getDusFromDom,
  normalizeNodeUid,
  normalizeActions,
  removeAnswerCommentFromUI,
  updateDocumentUserState,
  patchDocumentUserState,
  waitForVisible,
  resolveAnswerCommentSectionElement,
  syncAllContinueButtonsForDocument,
  syncContinueButtonForCardSection,
  mergeDocumentUserStateForContinueSync,
  getDusEntryForUid,
  dispatchCaseExperienceRefreshContinueButtonsIfTwoCol,
} from '../../utils/case_experience/case_experience_in_rails';
import { removeSpeechToTextToolbarFromContainer } from '../../utils/case_experience_in_rails';
import { renderCaseExperienceAquiButtonHtml } from '../../utils/case_experience/aqui_button_html';

const MAX_SUMMARY_STATEMENT_TIME_MS = 60000;

// Survives Turbo remounts so the AI error modal can stay visible while autosubmit runs.
let pendingSummaryStatementAiErrorModal = null;

export function resetSummaryStatementAiErrorModalBridgeForTests() {
  pendingSummaryStatementAiErrorModal = null;
}

function pendingSummaryStatementAiErrorModalKey(documentId, nodeUid) {
  return `${documentId}:${normalizeNodeUid(nodeUid)}`;
}

export default class extends Controller {
  static targets = [
    'aiSummaryFeedbackBlock',
    'summaryFeedbackStream',
    'textarea',
    'infoModal',
    'errorModal',
    'gradedFeedback',
    'container',
    'dynamicButtonPlaceholder',
    'charCountWrapper',
    'disclaimer',
    'answerCommentBlockTemplate',
    'answerCommentBodyTemplate',
    'ratingInput',
    'commentInput',
    'commentCharCount',
    'commentSubmit',
  ];

  static values = {
    documentId: Number,
    userId: Number,
    nodeUid: String,
    feedbackState: Object,
    characterLimit: Number,
    idealSummary: { type: String, default: '' },
  };

  aiFeedbackState = {
    requested: false,
    loading: false,
    received: false,
    error: null,
    feedback: '',
    userRating: 0,
    userComment: '',
    userRatingSubmitted: false,
  };

  _summaryStatementNodeStateEntry() {
    return getDusEntryForUid(this.userSectionStateValue || {}, this.nodeUidValue);
  }

  _hasPersistedAiSummaryResponseError() {
    const errorRaw = this.feedbackStateValue?.aiSummaryStatementResponseError?.value;
    if (errorRaw != null && String(errorRaw).trim().length > 0) return true;

    const dusError = this._summaryStatementNodeStateEntry()?.aiSummaryStatementResponseError?.value;
    return dusError != null && String(dusError).trim().length > 0;
  }

  _isGradedSummaryStatement() {
    if (this.feedbackStateValue?.graded?.value === true) return true;
    return this._summaryStatementNodeStateEntry()?.graded?.value === true;
  }

  _isAiRequestFeedbackExplicitlyOff() {
    if (this.feedbackStateValue?.aiSummaryStatementRequestFeedback?.value === false) {
      return true;
    }
    return this._summaryStatementNodeStateEntry()?.aiSummaryStatementRequestFeedback?.value === false;
  }

  _shouldRequestAiFeedback() {
    if (this._hasPersistedAiSummaryResponseError()) return false;
    if (this._isGradedSummaryStatement()) return false;
    if (this._isAiRequestFeedbackExplicitlyOff()) return false;
    return true;
  }

  _isAiFeedbackRequested() {
    return this._aiFeedbackRequested !== false;
  }

  applyDefaultAiFeedbackMode() {
    const shouldRequestFeedback = this._shouldRequestAiFeedback();
    this._aiFeedbackRequested = shouldRequestFeedback;
    if (shouldRequestFeedback && !this.hasServerRenderedFeedbackPanel()) {
      this.setAiFeedbackRequested(true, { persist: true });
    } else if (!shouldRequestFeedback) {
      this.setAiFeedbackRequested(false, { persist: false });
    }
  }

  hasServerRenderedFeedbackPanel() {
    if (!this.hasSummaryFeedbackStreamTarget) return false;
    return Boolean(this.summaryFeedbackStreamTarget.querySelector('.ai-two-column-wrapper'));
  }

  _hasNewAnswerCommentInStream() {
    if (!this.hasSummaryFeedbackStreamTarget) return false;
    return Boolean(
      this.summaryFeedbackStreamTarget.querySelector('.answer-comment-placeholder'),
    );
  }

  _answerCommentUidFromTemplate() {
    if (this.hasAnswerCommentBlockTemplateTarget) {
      return this.answerCommentBlockTemplateTarget.dataset.answerCommentUid || null;
    }
    if (this.hasAnswerCommentBodyTemplateTarget) {
      return this.answerCommentBodyTemplateTarget.dataset.answerCommentUid || null;
    }
    return `${normalizeNodeUid(this.nodeUidValue)}_ANS_COMMENT`;
  }

  _hideLegacyAnswerCommentSections() {
    const legacy = this.getAnswerCommentSiblingElement();
    if (legacy) {
      legacy.classList.add('hidden');
      legacy.style.display = 'none';
    }

    const slotUid = `${normalizeNodeUid(this.nodeUidValue)}_ANS_COMMENT`;
    const slotEl = document.getElementById(`slot-${slotUid}`);
    slotEl
      ?.querySelectorAll('.doc-section.answer_comment, .answer_comment')
      .forEach((el) => {
        el.classList.add('hidden');
        el.style.display = 'none';
      });

    const card = this.element.closest('.summary-statement-l1-card');
    card?.querySelectorAll('.doc-section.answer_comment').forEach((el) => {
      if (el.closest('.answer-comment-placeholder')) return;
      el.classList.add('hidden');
      el.style.display = 'none';
    });
  }

  _revealNewSummaryStatementAnswerCommentInStream() {
    if (!this.hasSummaryFeedbackStreamTarget) return;
    if (this._hasNewAnswerCommentInStream()) return;
    if (!this.hasAnswerCommentBlockTemplateTarget) return;

    const fragment = this.answerCommentBlockTemplateTarget.content.cloneNode(true);
    this.summaryFeedbackStreamTarget.appendChild(fragment);
  }

  lockSummaryStatementAfterNonAiSubmit({ afterSubmit = false } = {}) {
    if (!this._isSummaryStatementExperience()) return;

    this._hideLegacyAnswerCommentSections();
    this._revealNewSummaryStatementAnswerCommentInStream();

    if (this.hasTextareaTarget) {
      this.textareaTarget.disabled = true;
    }
    removeSpeechToTextToolbarFromContainer(this.element);
    if (this.hasDisclaimerTarget && this.disclaimerTarget) {
      this.disclaimerTarget.style.display = 'none';
    }
    this._hideSiblingSubmitButton();
    if (this.hasDynamicButtonPlaceholderTarget) {
      this.dynamicButtonPlaceholderTarget.style.display = 'none';
    }
    const answerControls = this.element.querySelector('.summary-statement-answer-controls');
    if (answerControls) {
      answerControls.style.display = 'none';
    }

    const answerCommentUid = this._answerCommentUidFromTemplate();
    if (afterSubmit && answerCommentUid) {
      const uidKey = normalizeNodeUid(answerCommentUid);
      const now = Date.now();
      void updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body: {
          document_user_state: {
            state: {
              [uidKey]: {
                invisible: { value: false, updatedAt: now },
              },
            },
          },
        },
      }).then(() => {
        this.userSectionStateValue = getDusFromDom() || {};
      });
    }
    if (answerCommentUid) {
      this.removeInvisiblePropertyFromOnClickFromSubmitButton(answerCommentUid);
    }
  }

  getAnswerCommentRootFromMemory() {
    if (!this.answerCommentHtml) return null;
    const doc = new DOMParser().parseFromString(this.answerCommentHtml, 'text/html');
    return doc.querySelector('.doc-section.answer_comment');
  }

  getAnswerCommentIdFromStoredHtml() {
    const root = this.getAnswerCommentRootFromMemory();
    return root?.id || null;
  }

  extractIdealFromAnswerCommentRoot(root) {
    if (!root) return '';
    const sectionBody = root.querySelector('.doc-section-body');
    if (!sectionBody) return '';
    const docChildren = sectionBody.querySelector('.doc-children');
    if (!docChildren) return '';
    const firstParagraph = docChildren.querySelector('.doc-paragraph');
    return firstParagraph ? firstParagraph.innerText.trim() : '';
  }

  revealAnswerCommentForSwitchOffAfterTurbo() {
    if (!this.element.isConnected) return;
    if (this._isSummaryStatementExperience()) {
      this.lockSummaryStatementAfterNonAiSubmit({ afterSubmit: true });
      return;
    }

    let answerCommentSibling = null;
    if (this.element.parentElement) {
      answerCommentSibling = Array.from(this.element.parentElement.children).find(
        (el) => el.classList && el.classList.contains('answer_comment'),
      );
    }
    if (answerCommentSibling) {
      const removeClassesRecursively = (el) => {
        if (el.classList) {
          el.classList.remove('hidden', 'collapsed');
        }
        for (const child of el.children || []) {
          removeClassesRecursively(child);
        }
      };
      removeClassesRecursively(answerCommentSibling);
      answerCommentSibling.style.removeProperty('display');
      void answerCommentSibling.offsetHeight;
    }
    let ac = answerCommentSibling;
    if (!ac && this.element.parentElement) {
      ac = Array.from(this.element.parentElement.children).find(
        (el) => el.classList && el.classList.contains('answer_comment'),
      );
    }
    const settledAc = ac || answerCommentSibling;
    const derivedAnswerCommentUid = `${normalizeNodeUid(this.nodeUidValue)}_ANS_COMMENT`;
    const answerCommentId = settledAc?.id || derivedAnswerCommentUid;
    if (answerCommentId) {
      const uidKey = normalizeNodeUid(answerCommentId);
      const now = Date.now();
      void updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body: {
          document_user_state: {
            state: {
              [uidKey]: {
                invisible: { value: false, updatedAt: now },
              },
            },
          },
        },
      }).then(() => {
        this.userSectionStateValue = getDusFromDom() || {};
      });
      this.removeInvisiblePropertyFromOnClickFromSubmitButton(answerCommentId);
    }
  }

  isStudentDisplayMode() {
    const docBody = this.element.closest('.doc-body');
    return Boolean(docBody && docBody.classList.contains('student-display-mode'));
  }

  modalControllerFor(containerTarget) {
    if (!containerTarget) return null;
    const root = containerTarget.closest('[data-controller~="railsui-modal"]');
    if (!root) return null;
    return this.application.getControllerForElementAndIdentifier(root, 'railsui-modal');
  }

  openModalElement(containerTarget, { onOutsideDismiss } = {}) {
    if (onOutsideDismiss) {
      this._bindSummaryStatementModalOutsideDismiss(containerTarget, onOutsideDismiss);
    }
    const controller = this.modalControllerFor(containerTarget);
    if (controller) {
      controller.show();
      return;
    }
    containerTarget.classList.remove('hidden');
    containerTarget.classList.add('aquifer-modal-overlay');
    containerTarget.setAttribute('aria-hidden', 'false');
  }

  closeModalElement(containerTarget) {
    this._unbindSummaryStatementModalOutsideDismiss(containerTarget);
    const controller = this.modalControllerFor(containerTarget);
    if (controller) {
      controller.close();
      return;
    }
    containerTarget.classList.add('hidden');
    containerTarget.classList.remove('aquifer-modal-overlay');
    containerTarget.setAttribute('aria-hidden', 'true');
  }

  _isEscapeKey(event) {
    return event.key === 'Escape' || event.keyCode === 27;
  }

  dismissErrorModalFromKeyboard(event) {
    if (!this._isEscapeKey(event)) return;
    if (!this.hasErrorModalTarget || !this.isModalOpen(this.errorModalTarget)) return;

    event.preventDefault();
    event.stopPropagation();
    void this.closeErrorModal();
  }

  dismissInfoModalFromKeyboard(event) {
    if (!this._isEscapeKey(event)) return;
    if (!this.hasInfoModalTarget || !this.isModalOpen(this.infoModalTarget)) return;

    event.preventDefault();
    event.stopPropagation();
    this.infoModalProceedOnClose = false;
    void this.closeInfoModal();
  }

  _bindSummaryStatementModalOutsideDismiss(containerTarget, onDismiss) {
    this._unbindSummaryStatementModalOutsideDismiss(containerTarget);

    const modalRoot = containerTarget.closest('[data-controller~="railsui-modal"]');
    this._disableRailsuiModalDefaultDismiss(containerTarget, modalRoot);

    this._onSummaryStatementModalOutsideClick = (event) => {
      if (!this.isModalOpen(containerTarget)) return;

      const content = modalRoot?.querySelector('[data-railsui-modal-target="content"]');
      if (content?.contains(event.target)) return;
      if (!containerTarget.contains(event.target)) return;

      event.preventDefault();
      event.stopPropagation();
      void onDismiss();
    };

    containerTarget.addEventListener('mousedown', this._onSummaryStatementModalOutsideClick, true);
    this._summaryStatementModalOutsideDismissContainer = containerTarget;
  }

  _unbindSummaryStatementModalOutsideDismiss(containerTarget) {
    if (
      this._onSummaryStatementModalOutsideClick &&
      this._summaryStatementModalOutsideDismissContainer === containerTarget
    ) {
      this._summaryStatementModalOutsideDismissContainer.removeEventListener(
        'mousedown',
        this._onSummaryStatementModalOutsideClick,
        true,
      );
    }
    if (this._summaryStatementModalOutsideDismissContainer === containerTarget) {
      this._summaryStatementModalOutsideDismissContainer = null;
    }
    this._onSummaryStatementModalOutsideClick = null;
  }

  _disableRailsuiModalDefaultDismiss(containerTarget, modalRoot) {
    const action = containerTarget.getAttribute('data-action') || '';
    const filtered = action
      .split(/\s+/)
      .filter((entry) => entry && !entry.includes('closeWithEsc'))
      .join(' ')
      .trim();

    if (filtered) containerTarget.setAttribute('data-action', filtered);
    else containerTarget.removeAttribute('data-action');

    if (modalRoot) {
      modalRoot.setAttribute('data-railsui-modal-click-outside-value', 'false');
    }

    const railsui = this.modalControllerFor(containerTarget);
    if (railsui) {
      railsui.clickOutsideValue = false;
    }
  }

  _prepareSummaryStatementModal(containerTarget) {
    const modalRoot = containerTarget.closest('[data-controller~="railsui-modal"]');
    this._disableRailsuiModalDefaultDismiss(containerTarget, modalRoot);
  }

  isModalOpen(containerTarget) {
    if (!containerTarget) return false;
    return !containerTarget.classList.contains('hidden');
  }

  connect() {
    this._suppressSummaryFeedbackAbortError = false;
    this.infoModalProceedOnClose = false;
    this.userSectionStateValue = getDusFromDom() || {};
    this._aiFeedbackRequested = this._shouldRequestAiFeedback();

    if (this._isSummaryStatementExperience()) {
      this._hideSiblingSubmitButton();
    }

    waitForVisible(currentSection(this.element), () => this.applyDefaultAiFeedbackMode());

    if (this.hasInfoModalTarget) {
      this._prepareSummaryStatementModal(this.infoModalTarget);
      this.closeModalElement(this.infoModalTarget);
    }
    if (this.hasErrorModalTarget) {
      this._prepareSummaryStatementModal(this.errorModalTarget);
      if (!this._hasPendingErrorModalForThisNode()) {
        this.closeModalElement(this.errorModalTarget);
      }
    }

    this.state = this.feedbackStateValue || {};
    this.aiFeedbackState = {
      ...this.aiFeedbackState,
      requested: !!this.state.aiSummaryStatementRequestFeedback?.value,
      loading: !!this.state.aiSummaryStatementLoadingFeedback?.value,
      received: false,
      error: this.state.aiSummaryStatementResponseError?.value || null,
      feedback: this.state.aiSummaryStatementFeedback?.value || '',
      userRating: this.state.aiSummaryStatementUserRating?.value || 0,
      userComment: this.state.aiSummaryStatementUserComment?.value || '',
      userRatingSubmitted: !!this.state.aiSummaryStatementUserRatingSubmitted?.value,
    };

    const feedbackRaw = this.feedbackStateValue?.aiSummaryStatementFeedback?.value;
    const hasFeedbackContent =
      feedbackRaw != null && String(feedbackRaw).trim().length > 0;

    if (hasFeedbackContent || this.hasServerRenderedFeedbackPanel()) {
      this.hydrateFromServerRenderedFeedbackPanel(hasFeedbackContent ? feedbackRaw : null);
    } else {
      if (this.feedbackStateValue?.graded?.value) {
        if (this._isSummaryStatementExperience()) {
          this.lockSummaryStatementAfterNonAiSubmit({ afterSubmit: false });
        }
      }
      if (this.hasGradedFeedbackTarget) {
        this.gradedFeedbackTarget.style.display = 'none';
      }
      const submitButton = this.getSiblingButton();
      if (submitButton) {
        setTimeout(() => {
          if (
            this._isSummaryStatementExperience() &&
            this.feedbackStateValue?.graded?.value
          ) {
            submitButton.disabled = true;
            return;
          }
          submitButton.disabled = !(
            this.textareaTarget &&
            this.textareaTarget.value &&
            this.textareaTarget.value.trim().length > 0
          );
        }, 0);
      }
      if (this.hasDynamicButtonPlaceholderTarget) {
        const existingDynamic = this.dynamicButtonPlaceholderTarget.querySelector('.ai-dynamic-submit');
        if (existingDynamic) {
          this.dynamicButton = existingDynamic;
          this._wireDynamicSubmitButton(existingDynamic, {
            aiFeedbackMode: this._isAiFeedbackRequested(),
          });
        }
      }
    }

    this.applyAnswerCommentSiblingChrome();

    this._submitButton = this.getSiblingButton();
    this._handleSubmitClick = async () => {
      if (
        this.aiFeedbackState.received &&
        this.aiFeedbackState.userRating > 0 &&
        !this.aiFeedbackState.userRatingSubmitted
      ) {
        const now = Date.now();
        try {
          await patchDocumentUserState({
            documentId: this.documentIdValue,
            userId: this.userIdValue,
            body: {
              document_user_state: {
                state: {
                  [this.nodeUidValue]: {
                    aiSummaryStatementUserRating: {
                      value: this.aiFeedbackState.userRating,
                      updatedAt: now,
                    },
                    aiSummaryStatementUserComment: {
                      value: this.aiFeedbackState.userComment || '',
                      updatedAt: now,
                    },
                    aiSummaryStatementUserRatingSubmitted: { value: true, updatedAt: now },
                  },
                },
              },
            },
          });
          this.aiFeedbackState.userRatingSubmitted = true;
          this.userSectionStateValue = getDusFromDom() || {};
          this.disableRating();
          this.syncSubmitButtonFromRating();
          this._enableSummaryStatementContinueAfterFeedback();
        } catch (e) {
          console.error(e);
        }
      }
      const aiFeedbackOn = this._isAiFeedbackRequested();
      if (aiFeedbackOn) {
        setTimeout(() => {
          if (!this.element.isConnected) return;
          this.disableRating();
          if (this.hasDisclaimerTarget) {
            this.disclaimerTarget.style.display = 'none';
          }
        }, 100);
      }
    };
    if (this._submitButton) {
      this._submitButton.addEventListener('click', this._handleSubmitClick);
    }

    requestAnimationFrame(() => this._restorePendingErrorModalIfNeeded());
  }

  hydrateFromServerRenderedFeedbackPanel(feedbackRaw) {
    if (feedbackRaw != null) {
      this.aiFeedbackState.received = true;
      this.aiFeedbackState.feedback = feedbackRaw;
    } else if (this.hasSummaryFeedbackStreamTarget) {
      const feedbackBox =
        this.summaryFeedbackStreamTarget.querySelector(`#ai-feedback-${this.nodeUidValue}`) ||
        this.summaryFeedbackStreamTarget.querySelector('.ai-feedback-box');
      if (feedbackBox?.value) {
        this.aiFeedbackState.received = true;
        this.aiFeedbackState.feedback = feedbackBox.value;
      }
    }
    this.syncRatingFromDom();
    this.applyChromeForSummaryFeedbackLayout();
    this.configureSubmitAfterFeedback();
    this.scheduleAutoResizeFeedbackTextarea(this.aiSummaryFeedbackBlockTarget);
    this.maybeRemoveInvisibleFromSubmitOnClick();
    if (this.aiFeedbackState.userRatingSubmitted) {
      this.disableRating();
      this.disableSubmitIfNextContinueIsEnabledOrNextSectionIsVisible();
    } else {
      this.syncSubmitButtonFromRating();
    }
  }

  syncRatingFromDom() {
    if (this.hasRatingInputTarget) {
      const rating = parseInt(this.ratingInputTarget.value || '0', 10);
      if (!Number.isNaN(rating)) {
        this.aiFeedbackState.userRating = rating;
      }
    }
    if (this.hasCommentInputTarget) {
      this.aiFeedbackState.userComment = this.commentInputTarget.value || '';
      this._updateCommentCharCount();
    }
  }

  syncSubmitButtonFromRating() {
    const ratingGiven = this.aiFeedbackState.userRating && this.aiFeedbackState.userRating > 0;
    const disabled = this.aiFeedbackState.userRatingSubmitted || !ratingGiven;

    const submitButton = this.getSiblingButton();
    if (submitButton) submitButton.disabled = disabled;

    if (this.hasCommentSubmitTarget) {
      this.commentSubmitTarget.disabled = disabled;
    }
  }

  async submitComment(event) {
    if (event) event.preventDefault();
    if (this._handleSubmitClick) await this._handleSubmitClick();
  }

  _buildSummaryStatementContinueSyncState() {
    const domState = getDusFromDom() || {};
    const nodeKey = normalizeNodeUid(this.nodeUidValue);
    const existing = getDusEntryForUid(domState, nodeKey) || {};
    const feedbackText =
      existing.aiSummaryStatementFeedback?.value ||
      this.feedbackStateValue?.aiSummaryStatementFeedback?.value ||
      this.aiFeedbackState.feedback ||
      '';

    const nodePatch = {
      ...existing,
      graded: existing.graded?.value === true ? existing.graded : { value: true },
      aiSummaryStatementUserRatingSubmitted: { value: true },
      aiSummaryStatementUserRating: {
        value: this.aiFeedbackState.userRating,
      },
    };

    if (feedbackText) {
      nodePatch.aiSummaryStatementFeedback = { value: String(feedbackText) };
    }
    if (existing.aiSummaryStatementResponseRecieved?.value === true) {
      nodePatch.aiSummaryStatementResponseRecieved = existing.aiSummaryStatementResponseRecieved;
    } else {
      nodePatch.aiSummaryStatementResponseRecieved = { value: true };
    }

    return mergeDocumentUserStateForContinueSync(domState, { [nodeKey]: nodePatch });
  }

  _enableSummaryStatementContinueAfterFeedback() {
    const mergedState = this._buildSummaryStatementContinueSyncState();
    const application = typeof window !== 'undefined' ? window.Stimulus : null;
    const section =
      this.element.closest('[data-case-experience--case-viewer-target="section"]') ||
      this.element.closest('.summary-statement-l1-card') ||
      this.element.closest('.summary-statement-root-section');

    if (section && application) {
      syncContinueButtonForCardSection(section, application, mergedState);
    }

    syncAllContinueButtonsForDocument(mergedState);
    dispatchCaseExperienceRefreshContinueButtonsIfTwoCol();

    if (this._continueAfterFeedbackTurboHandler) return;

    this._continueAfterFeedbackTurboHandler = () => {
      if (!this.aiFeedbackState.userRatingSubmitted) return;
      const state = this._buildSummaryStatementContinueSyncState();
      syncAllContinueButtonsForDocument(state);
    };
    document.addEventListener('turbo:after-stream-render', this._continueAfterFeedbackTurboHandler);
  }

  maybeRemoveInvisibleFromSubmitOnClick() {
    const answerCommentUid = this._answerCommentUidFromTemplate();
    if (
      answerCommentUid &&
      (this.hasServerRenderedFeedbackPanel() || this._hasNewAnswerCommentInStream())
    ) {
      this.removeInvisiblePropertyFromOnClickFromSubmitButton(answerCommentUid);
    }
  }

  applyAnswerCommentSiblingChrome() {
    if (this._isSummaryStatementExperience()) {
      const legacy = this.getAnswerCommentSiblingElement();
      const legacyBody = legacy?.querySelector('.doc-section-body');
      if (legacyBody && legacyBody.offsetParent !== null) {
        this.lockSummaryStatementAfterNonAiSubmit({ afterSubmit: false });
      } else if (this._hasNewAnswerCommentInStream()) {
        this.lockSummaryStatementAfterNonAiSubmit({ afterSubmit: false });
      }
      return;
    }

    const answerCommentSibling = this.getAnswerCommentSiblingElement();
    const answerCommentBody =
      answerCommentSibling && answerCommentSibling.querySelector('.doc-section-body');
    if (
      this.isStudentDisplayMode() &&
      answerCommentBody &&
      answerCommentBody.offsetParent !== null
    ) {
      if (this.hasDisclaimerTarget && this.disclaimerTarget) {
        this.disclaimerTarget.style.display = 'none';
      }
      const submitButton = this.getSiblingButton();
      if (submitButton) {
        setTimeout(() => {
          submitButton.disabled = true;
        }, 0);
      }
    }
  }

  disconnect() {
    if (this.hasErrorModalTarget) {
      this._unbindSummaryStatementModalOutsideDismiss(this.errorModalTarget);
    }
    if (this.hasInfoModalTarget) {
      this._unbindSummaryStatementModalOutsideDismiss(this.infoModalTarget);
    }
    if (this._continueAfterFeedbackTurboHandler) {
      document.removeEventListener('turbo:after-stream-render', this._continueAfterFeedbackTurboHandler);
      this._continueAfterFeedbackTurboHandler = null;
    }
    if (this._submitButton && this._handleSubmitClick) {
      this._submitButton.removeEventListener('click', this._handleSubmitClick);
    }
    this._submitButton = null;
    this._handleSubmitClick = null;

    if (this._summaryFeedbackAbortController) {
      this._suppressSummaryFeedbackAbortError = true;
      try {
        this._summaryFeedbackAbortController.abort();
      } catch (_) {
        /* ignore */
      }
      this._summaryFeedbackAbortController = null;
    }

    clearTimeout(this._feedbackTextareaResizeTimeout);
    this._feedbackTextareaResizeTimeout = null;
    this._feedbackTextareaResizeContainer = null;
  }

  setSmileRating(event) {
    const rating = parseInt(event.params.rating, 10);
    if (Number.isNaN(rating)) return;

    if (this.hasRatingInputTarget) this.ratingInputTarget.value = String(rating);
    this.aiFeedbackState.userRating = rating;

    const submitButton = this.getSiblingButton();
    if (submitButton) submitButton.disabled = false;
    if (this.hasCommentSubmitTarget) this.commentSubmitTarget.disabled = false;
  }

  updateComment() {
    if (!this.hasCommentInputTarget) return;
    this.aiFeedbackState.userComment = this.commentInputTarget.value;
    this._updateCommentCharCount();
  }

  _updateCommentCharCount() {
    if (!this.hasCommentCharCountTarget || !this.hasCommentInputTarget) return;
    const limit = this.characterLimitValue || this.commentInputTarget.maxLength || 0;
    const count = this.commentInputTarget.value.length;
    this.commentCharCountTarget.textContent = `${count}/${limit}`;
  }

  disableSubmitIfNextContinueIsEnabledOrNextSectionIsVisible() {
    const submitButtonContainer = this.getSiblingSubmitButtonContainer();
    const submitButton = this.getSiblingButton();

    if (this.hasTextareaTarget) {
      this.textareaTarget.disabled = true;
    }
    removeSpeechToTextToolbarFromContainer(this.element);

    if (submitButtonContainer) {
      const onClickValue = submitButtonContainer.getAttribute('data-case-experience--button-action-on-click-value');

      if (onClickValue) {
        const actions = JSON.parse(onClickValue);
        const continueAction = actions.find(
          (a) =>
            a.type === 'action' &&
            a.arguments.key === 'inactive' &&
            a.arguments.value === 1 &&
            a.action === 'setNodeKVPair',
        );
        const continueButtonContainerUid = continueAction ? continueAction.arguments.uid : null;

        if (continueButtonContainerUid) {
          const continueButtonContainer = document.getElementById(continueButtonContainerUid);
          const continueButton =
            continueButtonContainer && continueButtonContainer.querySelector('button');

          if (
            continueButtonContainer &&
            continueButtonContainer.getAttribute('data-case-experience--button-action-inactive-value') === '1' &&
            continueButton &&
            continueButton.innerText.trim().toLowerCase() === 'continue'
          ) {
            setTimeout(() => {
              submitButton.disabled = true;
            }, 100);
            this.disableRating();
          }

          const continueButtonActions = continueButtonContainer.getAttribute(
            'data-case-experience--button-action-on-click-value',
          );
          if (continueButtonActions) {
            const continueActions = JSON.parse(continueButtonActions);
            const showSectionAction = continueActions.find(
              (a) =>
                a.type === 'action' &&
                a.action === 'setNodeKVPair' &&
                a.arguments.key === 'invisible' &&
                a.arguments.value === false,
            );
            const sectionToShowUid = showSectionAction ? showSectionAction.arguments.uid : null;
            if (sectionToShowUid) {
              const sectionToShowElement = document.getElementById(sectionToShowUid);
              if (sectionToShowElement && sectionToShowElement.offsetParent !== null) {
                setTimeout(() => {
                  submitButton.disabled = true;
                }, 0);
                this.disableRating();
              }
            }
          }
        }
      }
    }
  }

  getAnswerCommentSiblingElement() {
    const found = resolveAnswerCommentSectionElement(this.element, this.nodeUidValue);
    this._answerCommentSibling = found || null;
    return found || null;
  }

  _isLegacySubmitButtonSlice(element) {
    if (!element || element === this.element || this.element.contains(element)) {
      return false;
    }
    if (!element.classList?.contains('doc-button-slice')) {
      return false;
    }
    const controllers = element.getAttribute('data-controller') || '';
    if (!controllers.includes('case-experience--button-action')) {
      return false;
    }
    return element.getAttribute('data-case-experience--button-action-name-value') === 'submit';
  }

  getSiblingSubmitButtonContainer() {
    if (
      this.siblingSubmitButtonContainer &&
      document.body.contains(this.siblingSubmitButtonContainer)
    ) {
      return this.siblingSubmitButtonContainer;
    }

    const root = this.hasContainerTarget ? this.containerTarget : this.element;
    if (root.parentElement) {
      for (const sibling of root.parentElement.children) {
        if (this._isLegacySubmitButtonSlice(sibling)) {
          this.siblingSubmitButtonContainer = sibling;
          return sibling;
        }
      }
    }

    if (this._isSummaryStatementExperience()) {
      const section = this.element.closest('.summary-statement-l1-card');
      if (section) {
        for (const candidate of section.querySelectorAll(
          '.doc-button-slice[data-case-experience--button-action-name-value="submit"]',
        )) {
          if (this._isLegacySubmitButtonSlice(candidate)) {
            this.siblingSubmitButtonContainer = candidate;
            return candidate;
          }
        }
      }
    }

    this.siblingSubmitButtonContainer = null;
    return null;
  }

  getSiblingButton() {
    const buttonContainer = this.getSiblingSubmitButtonContainer();
    if (buttonContainer) {
      return buttonContainer.querySelector('button');
    }
    return null;
  }

  _backendAiSummaryRequestFeedbackValue() {
    const uid = this.nodeUidValue;
    const state = this.userSectionStateValue || {};
    const entry =
      state[uid] || state[uid.replace(/-/g, ' ')] || state[uid.replace(/ /g, '-')];
    return !!entry?.aiSummaryStatementRequestFeedback?.value;
  }

  async _persistAiSummaryResponseError(message) {
    const now = Date.now();
    const value = message == null || message === '' ? null : String(message);
    try {
      await updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body: {
          document_user_state: {
            state: {
              [this.nodeUidValue]: {
                aiSummaryStatementResponseError: { value, updatedAt: now },
              },
            },
          },
        },
      });
      this.userSectionStateValue = getDusFromDom() || {};
    } catch (e) {
      console.error(e);
    }
  }

  _notesController() {
    const root = this.hasContainerTarget ? this.containerTarget : this.element;
    return this.application.getControllerForElementAndIdentifier(root, 'case-experience--notes');
  }

  _syncNotesSubmitButtonState() {
    this._notesController()?.reconcileVisibleSubmitButton?.();
  }

  _isSummaryStatementExperience() {
    const root = this.hasContainerTarget ? this.containerTarget : this.element;
    return root.classList.contains('doc-case-experience--summary-statement');
  }

  shouldLockAfterNonAiSubmit() {
    return this._isSummaryStatementExperience() && !this._isAiFeedbackRequested();
  }

  _hideSiblingSubmitButton() {
    const originalButtonContainer = this.getSiblingSubmitButtonContainer();
    if (originalButtonContainer) {
      originalButtonContainer.style.display = 'none';
      const origBtn = originalButtonContainer.querySelector('button');
      if (origBtn) origBtn.style.display = 'none';
    }
  }

  _normalizeSummaryStatementSubmitButton(button) {
    if (!button) return;

    button.classList.remove('btn-primary', 'summary-statement-submit--submitting');
    button.classList.add('btn-secondary');
    button.removeAttribute('aria-busy');
    button.querySelector('.aqui-loading-dots')?.remove();
    const label = button.querySelector('.btn-label');
    if (label) label.textContent = 'Submit Your Answer';
    button.setAttribute('aria-label', 'Submit Your Answer');
  }

  _triggerLegacySummaryStatementSubmit() {
    const siblingBtn = this.getSiblingButton();
    if (siblingBtn) {
      siblingBtn.click();
      return;
    }

    this._notesController()?.handleSubmitClick?.();
  }

  ensureDynamicSubmitButton({ aiFeedbackMode = true } = {}) {
    if (!this.hasDynamicButtonPlaceholderTarget) return;

    let button = this.dynamicButtonPlaceholderTarget.querySelector('.ai-dynamic-submit');
    if (!button) {
      this.dynamicButtonPlaceholderTarget.innerHTML = renderCaseExperienceAquiButtonHtml({
        label: 'Submit Your Answer',
        action: null,
        extraClass: 'ai-dynamic-submit',
        variant: this._isSummaryStatementExperience() ? 'secondary' : 'primary',
      });
      button = this.dynamicButtonPlaceholderTarget.querySelector('.ai-dynamic-submit');
    }

    if (!button) return;

    if (this._isSummaryStatementExperience()) {
      this._normalizeSummaryStatementSubmitButton(button);
    }

    this.dynamicButton = button;
    this._wireDynamicSubmitButton(button, { aiFeedbackMode });
  }

  setAiFeedbackRequested(enabled, { persist = true } = {}) {
    const checked = !!enabled;
    this._aiFeedbackRequested = checked;

    if (this._isSummaryStatementExperience()) {
      this._hideSiblingSubmitButton();
      this.ensureDynamicSubmitButton({ aiFeedbackMode: checked });
      this._syncNotesSubmitButtonState();
    } else if (checked) {
      this._hideSiblingSubmitButton();
      this.addDynamicSubmitButton();
      this._syncNotesSubmitButtonState();
    } else {
      const originalButtonContainer = this.getSiblingSubmitButtonContainer();
      if (originalButtonContainer) {
        originalButtonContainer.style.display = '';
        const origBtn = originalButtonContainer.querySelector('button');
        if (origBtn) origBtn.style.display = '';
      }
      this.removeDynamicSubmitButton();
      this._syncNotesSubmitButtonState();
    }

    if (!persist) return;

    const backendVal = this._backendAiSummaryRequestFeedbackValue();
    if (backendVal !== checked) {
      void this._persistAiSummaryToggleState(checked);
    }
  }

  async _persistAiSummaryToggleState(checked) {
    await this._notesController()?.flushPendingSave?.();

    const nodePatch = {
      aiSummaryStatementRequestFeedback: {
        value: checked,
        updatedAt: Date.now(),
      },
    };
    if (this.hasTextareaTarget) {
      nodePatch.value = {
        value: this.textareaTarget.value,
        updatedAt: Date.now(),
      };
    }

    try {
      await updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body: {
          document_user_state: {
            state: {
              [this.nodeUidValue]: nodePatch,
            },
          },
        },
      });
      this.userSectionStateValue = getDusFromDom() || {};
    } catch (error) {
      console.error(error);
    }
  }

  addDynamicSubmitButton() {
    if (this._isSummaryStatementExperience()) {
      this.ensureDynamicSubmitButton({ aiFeedbackMode: this._isAiFeedbackRequested() });
      return;
    }

    if (this.dynamicButton) {
      return;
    }
    if (!this.hasDynamicButtonPlaceholderTarget) return;

    const existing = this.dynamicButtonPlaceholderTarget.querySelector('.ai-dynamic-submit');
    if (existing) {
      this.dynamicButton = existing;
      this._wireDynamicSubmitButton(existing, { aiFeedbackMode: true });
      return;
    }

    this.dynamicButtonPlaceholderTarget.innerHTML = renderCaseExperienceAquiButtonHtml({
      label: 'Submit',
      action: null,
      extraClass: 'ai-dynamic-submit',
    });
    this.dynamicButton =
      this.dynamicButtonPlaceholderTarget.querySelector('.ai-dynamic-submit');
    if (this.dynamicButton) {
      this._wireDynamicSubmitButton(this.dynamicButton, { aiFeedbackMode: true });
    }
  }

  _wireDynamicSubmitButton(button, { aiFeedbackMode = true } = {}) {
    this._hideSiblingSubmitButton();

    if (button._aiSummaryDynamicClickHandler) {
      button.removeEventListener('click', button._aiSummaryDynamicClickHandler);
      button._aiSummaryDynamicClickHandler = null;
    }

    const handler = aiFeedbackMode
      ? () => this.onDynamicSubmit()
      : () => this._triggerLegacySummaryStatementSubmit();

    button.addEventListener('click', handler);
    button._aiSummaryDynamicClickHandler = handler;
    button.dataset.aiSummaryDynamicWired = 'true';
    button.dataset.aiSummaryDynamicMode = aiFeedbackMode ? 'ai' : 'legacy';
  }

  removeDynamicSubmitButton() {
    if (this._isSummaryStatementExperience()) {
      this.ensureDynamicSubmitButton({ aiFeedbackMode: false });
      return;
    }

    if (this.dynamicButton) {
      if (this.dynamicButton._aiSummaryDynamicClickHandler) {
        this.dynamicButton.removeEventListener(
          'click',
          this.dynamicButton._aiSummaryDynamicClickHandler,
        );
        this.dynamicButton._aiSummaryDynamicClickHandler = null;
      }
      this.dynamicButton.remove();
      this.dynamicButton = null;
    }
  }

  onDynamicSubmit() {
    this.infoModalProceedOnClose = true;
    this.openModalElement(this.infoModalTarget, {
      onOutsideDismiss: () => {
        this.infoModalProceedOnClose = false;
        return this.closeInfoModal();
      },
    });
  }

  async closeInfoModal() {
    const proceed = this.infoModalProceedOnClose;
    this.infoModalProceedOnClose = false;
    this.closeModalElement(this.infoModalTarget);
    if (!proceed) return;

    if (this._isSummaryStatementExperience()) {
      this.showSubmittingAnswerState();
      const submitButton = this.getSiblingButton();
      if (submitButton) submitButton.disabled = true;
      void this.requestAiSummaryFeedback();
      return;
    }

    this.applyChromeForSummaryFeedbackLayout();
    this.removeDynamicSubmitButton();
    const originalButton = this.getSiblingSubmitButtonContainer();
    if (originalButton) {
      originalButton.style.display = '';
    }
    const submitButton = this.getSiblingButton();
    if (submitButton) submitButton.disabled = true;
    await this.renderLoadingPanelViaTurbo();
    void this.requestAiSummaryFeedback();
  }

  showSubmittingAnswerState() {
    if (!this._isSummaryStatementExperience()) return;

    this.ensureDynamicSubmitButton({ aiFeedbackMode: true });
    const button =
      this.dynamicButton ||
      this.dynamicButtonPlaceholderTarget?.querySelector('.ai-dynamic-submit');
    if (!button) return;

    this.dynamicButton = button;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('summary-statement-submit--submitting');

    const label = button.querySelector('.btn-label');
    if (label) label.textContent = 'Submitting Answer';
    button.setAttribute('aria-label', 'Submitting Answer');

    if (!button.querySelector('.aqui-loading-dots')) {
      const dots = document.createElement('div');
      dots.className = 'aqui-loading-dots flex h-2 items-center gap-[5px] shrink-0';
      dots.setAttribute('aria-hidden', 'true');
      dots.innerHTML =
        '<span class="aqui-loading-dots__dot aqui-loading-dots__dot--1" aria-hidden="true"></span>' +
        '<span class="aqui-loading-dots__dot aqui-loading-dots__dot--2" aria-hidden="true"></span>' +
        '<span class="aqui-loading-dots__dot aqui-loading-dots__dot--3" aria-hidden="true"></span>';
      button.appendChild(dots);
    }

    if (this.hasCharCountWrapperTarget) {
      this.charCountWrapperTarget.style.display = 'none';
    }
    if (this.hasTextareaTarget) {
      this.textareaTarget.readOnly = true;
      this.textareaTarget.style.display = '';
    }
    removeSpeechToTextToolbarFromContainer(this.element);
  }

  clearSubmittingAnswerState() {
    if (
      this._autoSubmitAfterErrorInFlight ||
      this._hasPendingErrorModalForThisNode() ||
      (this.hasErrorModalTarget && this.isModalOpen(this.errorModalTarget))
    ) {
      return;
    }

    const button =
      this.dynamicButton ||
      this.dynamicButtonPlaceholderTarget?.querySelector('.ai-dynamic-submit');
    if (button) {
      this._normalizeSummaryStatementSubmitButton(button);
      const hasContent = Boolean(this.textareaTarget?.value?.trim());
      button.disabled = !hasContent;
    }
    if (this.hasTextareaTarget) {
      this.textareaTarget.readOnly = false;
    }
  }

  recoverFromTurboStreamError() {
    this._recoverFromAiFeedbackError('', false);
  }

  applyChromeForSummaryFeedbackLayout() {
    if (this.hasTextareaTarget) {
      this.textareaTarget.style.display = 'none';
    }
    removeSpeechToTextToolbarFromContainer(this.element);
    if (this.hasCharCountWrapperTarget) {
      this.charCountWrapperTarget.style.display = 'none';
    }
    if (this.hasGradedFeedbackTarget) {
      this.gradedFeedbackTarget.style.display = 'none';
    }
    if (this.hasDisclaimerTarget) {
      this.disclaimerTarget.style.display = 'none';
    }
  }

  async renderLoadingPanelViaTurbo() {
    const userSummary = encodeURIComponent(this.textareaTarget?.value || '');
    const url =
      `/documents/${encodeURIComponent(String(this.documentIdValue))}` +
      `/case_experience/ai_summary_feedback_panel` +
      `?node_uid=${encodeURIComponent(this.nodeUidValue)}` +
      `&phase=loading` +
      `&user_summary=${userSummary}` +
      `&character_limit=${encodeURIComponent(String(this.characterLimitValue))}`;

    try {
      const res = await fetch(url, {
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': getCsrfToken(),
        },
      });
      if (res.ok) {
        window.Turbo?.renderStreamMessage(await res.text());
        await new Promise(requestAnimationFrame);
        this.scheduleAutoResizeFeedbackTextarea(this.aiSummaryFeedbackBlockTarget);
      }
    } catch (err) {
      console.error(err);
    }
  }

  resolveIdealSummaryText() {
    let ideal = '';
    const fromMemory = this.getAnswerCommentRootFromMemory();
    if (fromMemory) {
      ideal = this.extractIdealFromAnswerCommentRoot(fromMemory);
    } else {
      const answerCommentSibling = this.getAnswerCommentSiblingElement();
      if (answerCommentSibling) {
        ideal = this.extractIdealFromAnswerCommentRoot(answerCommentSibling);
      }
    }
    if (!ideal.trim() && typeof this.idealSummaryValue === 'string' && this.idealSummaryValue.trim()) {
      ideal = this.idealSummaryValue.trim();
    }
    return ideal;
  }

  async requestAiSummaryFeedback() {
    if (this._summaryFeedbackFetchInFlight) return;
    this._summaryFeedbackFetchInFlight = true;
    this.aiFeedbackState.loading = true;

    const abortController = new AbortController();
    this._summaryFeedbackAbortController = abortController;
    const timeoutId = setTimeout(() => {
      abortController.abort();
    }, MAX_SUMMARY_STATEMENT_TIME_MS);

    const ideal = this.resolveIdealSummaryText();
    const userSummary = this.textareaTarget?.value || '';
    const answerCommentUid =
      this._answerCommentUidFromTemplate() ||
      this.getAnswerCommentSiblingElement()?.id ||
      '';

    const token = getCsrfToken();
    const body = new URLSearchParams();
    body.append('authenticity_token', token);
    body.append('ideal_summary_statement', ideal);
    body.append('user_summary_statement', userSummary);
    body.append('node_uid', this.nodeUidValue);
    body.append('character_limit', String(this.characterLimitValue));
    if (answerCommentUid) body.append('answer_comment_uid', answerCommentUid);

    try {
      const res = await fetch(
        `/documents/${encodeURIComponent(String(this.documentIdValue))}/automated_summary_statement_feedback`,
        {
          method: 'POST',
          redirect: 'manual',
          signal: abortController.signal,
          headers: {
            Accept: 'text/vnd.turbo-stream.html, application/json',
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-CSRF-Token': token,
          },
          body,
        },
      );
      const contentType = res.headers.get('content-type') || '';
      const text = await res.text();

      if (!res.ok) {
        this.aiFeedbackState.loading = false;
        let errMsg = `Request failed (${res.status})`;
        try {
          const payload = text ? JSON.parse(text) : null;
          if (payload && typeof payload === 'object' && payload.error) {
            errMsg = String(payload.error);
          } else if (payload && Array.isArray(payload.errors) && payload.errors.length > 0) {
            errMsg = String(payload.errors[0]);
          } else if (payload && typeof payload === 'object' && payload.message != null) {
            errMsg = String(payload.message);
          } else if (typeof payload === 'string' && payload.trim()) {
            errMsg = payload.trim();
          } else if (text && text.trim()) {
            errMsg = text.trim().slice(0, 500);
          }
        } catch {
          if (text && text.trim()) errMsg = text.trim().slice(0, 500);
        }
        this._recoverFromAiFeedbackError(errMsg);
        return;
      }

      if (contentType.includes('turbo-stream')) {
        window.Turbo?.renderStreamMessage(text);
        await new Promise(requestAnimationFrame);
        this.aiFeedbackState.loading = false;
        this.aiFeedbackState.received = true;
        this.aiFeedbackState.error = null;
        void this._persistAiSummaryResponseError(null);
        this.hydrateFromServerRenderedFeedbackPanel(null);
        return;
      }

      let payload = null;
      if (text) {
        try {
          payload = JSON.parse(text);
        } catch {
          payload = null;
        }
      }

      if (!payload || typeof payload !== 'object') {
        this.aiFeedbackState.loading = false;
        this._recoverFromAiFeedbackError('Invalid response from server');
        return;
      }

      const aiResponse = payload.ai_response != null ? String(payload.ai_response) : '';
      this.aiFeedbackState.loading = false;
      this.aiFeedbackState.received = true;
      this.aiFeedbackState.error = null;
      this.aiFeedbackState.feedback = aiResponse;
      void this._persistAiSummaryResponseError(null);
      this.hydrateFromServerRenderedFeedbackPanel(aiResponse);
    } catch (err) {
      console.error(err);
      this.aiFeedbackState.loading = false;
      if (err?.name === 'AbortError') {
        if (this._suppressSummaryFeedbackAbortError) {
          this._suppressSummaryFeedbackAbortError = false;
          return;
        }
        this._recoverFromAiFeedbackError('Connection Timeout');
        return;
      }
      this._recoverFromAiFeedbackError(err?.message || 'Network error');
    } finally {
      clearTimeout(timeoutId);
      this._summaryFeedbackAbortController = null;
      this._summaryFeedbackFetchInFlight = false;
    }
  }

  configureSubmitAfterFeedback() {
    const container = this.aiSummaryFeedbackBlockTarget;
    if (!container || !container.querySelector('.ai-rating-container')) return;
    if (this.dynamicButton) {
      this.removeDynamicSubmitButton();
    }
    const originalButtonContainer = this.getSiblingSubmitButtonContainer();
    if (originalButtonContainer) {
      originalButtonContainer.style.display = 'none';
      const origBtn = originalButtonContainer.querySelector('button');
      if (origBtn) origBtn.style.display = 'none';
    }
    setTimeout(() => this.syncSubmitButtonFromRating(), 100);
  }

  scheduleAutoResizeFeedbackTextarea(container) {
    if (!container) return;

    const signature = this._feedbackTextareaResizeSignature(container);
    if (
      signature === this._lastAppliedFeedbackTextareaSignature &&
      this._lastAppliedFeedbackTextareaHeight != null
    ) {
      return;
    }

    this._feedbackTextareaResizeContainer = container;
    clearTimeout(this._feedbackTextareaResizeTimeout);
    this._feedbackTextareaResizeTimeout = setTimeout(() => {
      this._feedbackTextareaResizeTimeout = null;
      const target = this._feedbackTextareaResizeContainer;
      this._feedbackTextareaResizeContainer = null;
      if (!target?.isConnected) return;
      this._applyAutoResizeFeedbackTextarea(target);
    }, 16);
  }

  _feedbackTextareaResizeSignature(container) {
    return Array.from(
      container.querySelectorAll('.summary-statement-feedback-field .form-textarea'),
    )
      .map((textarea) => `${textarea.value.length}:${textarea.value}`)
      .join('|');
  }

  _applyAutoResizeFeedbackTextarea(container) {
    const textareas = Array.from(
      container.querySelectorAll('.summary-statement-feedback-field .form-textarea'),
    );
    if (textareas.length === 0) return;

    const signature = this._feedbackTextareaResizeSignature(container);
    const minHeight = 138;
    const maxHeight = 320;

    textareas.forEach((textarea) => {
      textarea.style.height = 'auto';
    });

    const tallestContent = Math.max(
      minHeight,
      ...textareas.map((textarea) => textarea.scrollHeight),
    );
    const sharedHeight = Math.min(tallestContent, maxHeight);
    const needsScroll = textareas.some((textarea) => textarea.scrollHeight > sharedHeight);

    if (
      signature === this._lastAppliedFeedbackTextareaSignature &&
      sharedHeight === this._lastAppliedFeedbackTextareaHeight &&
      needsScroll === this._lastAppliedFeedbackTextareaNeedsScroll
    ) {
      return;
    }

    textareas.forEach((textarea) => {
      textarea.style.height = `${sharedHeight}px`;
      textarea.style.overflowY = needsScroll ? 'auto' : 'hidden';
    });

    this._lastAppliedFeedbackTextareaSignature = signature;
    this._lastAppliedFeedbackTextareaHeight = sharedHeight;
    this._lastAppliedFeedbackTextareaNeedsScroll = needsScroll;
  }

  removeInvisiblePropertyFromOnClickFromSubmitButton(answerCommentId) {
    const submitButtonContainer = this.getSiblingSubmitButtonContainer();
    if (submitButtonContainer) {
      const attr = submitButtonContainer.getAttribute('data-case-experience--button-action-on-click-value');
      if (attr) {
        let arr;
        try {
          arr = JSON.parse(attr);
        } catch {
          return;
        }
        const filteredArr = Array.isArray(arr)
          ? arr.filter(
              (obj) =>
                !(
                  obj &&
                  obj.action === 'setNodeKVPair' &&
                  obj.arguments &&
                  obj.arguments.key === 'invisible' &&
                  obj.arguments.uid === answerCommentId
                ),
            )
          : arr;
        submitButtonContainer.setAttribute(
          'data-case-experience--button-action-on-click-value',
          JSON.stringify(filteredArr),
        );
        submitButtonContainer.setAttribute(
          'data-case-experience--button-action-ai-summary-statement-answer-comment-shown-value',
          true,
        );
        const buttonController = this.application.getControllerForElementAndIdentifier(
          submitButtonContainer,
          'case-experience--button-action',
        );
        if (buttonController) {
          buttonController.onClickValue = filteredArr;
        }
      }
    }
  }

  showErrorModal() {
    this.openModalElement(this.errorModalTarget, {
      onOutsideDismiss: () => this.closeErrorModal(),
    });
  }

  async _persistAiSummaryFailureDismissal(errorMessage) {
    const now = Date.now();
    const value = errorMessage == null || errorMessage === '' ? null : String(errorMessage);

    this._syncAiSummaryRequestFeedbackStateOff();
    this._aiFeedbackRequested = false;

    try {
      await updateDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body: {
          document_user_state: {
            state: {
              [this.nodeUidValue]: {
                aiSummaryStatementResponseError: { value, updatedAt: now },
                aiSummaryStatementRequestFeedback: { value: false, updatedAt: now },
              },
            },
          },
        },
      });
      this.userSectionStateValue = getDusFromDom() || {};
    } catch (e) {
      console.error(e);
    }

    this._reassertAiFeedbackOffFromFeedbackState();
  }

  _reassertAiFeedbackOffFromFeedbackState() {
    this._syncAiSummaryRequestFeedbackStateOff();
    this._aiFeedbackRequested = false;
  }

  async closeErrorModal() {
    this.closeModalElement(this.errorModalTarget);
    if (pendingSummaryStatementAiErrorModal) {
      pendingSummaryStatementAiErrorModal.dismissed = true;
    }
    pendingSummaryStatementAiErrorModal = null;

    const deferredError = this._deferredErrorRecoveryErrorMessage;
    this._deferredErrorRecoveryErrorMessage = null;
    if (deferredError == null) return;

    return this._persistAiSummaryFailureDismissal(deferredError);
  }

  _hasPendingErrorModalForThisNode() {
    return (
      pendingSummaryStatementAiErrorModal != null &&
      !pendingSummaryStatementAiErrorModal.dismissed &&
      pendingSummaryStatementAiErrorModal.key ===
        pendingSummaryStatementAiErrorModalKey(this.documentIdValue, this.nodeUidValue)
    );
  }

  _restorePendingErrorModalIfNeeded() {
    if (!this._hasPendingErrorModalForThisNode()) return;
    if (!this.hasErrorModalTarget) return;
    if (this.isModalOpen(this.errorModalTarget)) return;

    this._deferredErrorRecoveryErrorMessage =
      pendingSummaryStatementAiErrorModal.message || '';
    this.showErrorModal();
  }

  _clearPendingErrorModalBridge() {
    if (pendingSummaryStatementAiErrorModal && !this._deferredErrorRecoveryErrorMessage) {
      this._deferredErrorRecoveryErrorMessage =
        pendingSummaryStatementAiErrorModal.message || '';
    }
    pendingSummaryStatementAiErrorModal = null;
  }

  async _autoSubmitAfterAiFeedbackError() {
    if (!this._isSummaryStatementExperience()) return;
    if (this._autoSubmitAfterErrorInFlight) return;

    const content = this.textareaTarget?.value?.trim();
    if (!content) return;
    if (this.feedbackStateValue?.graded?.value === true) return;

    this._autoSubmitAfterErrorInFlight = true;
    try {
      await this._quietNonAiSubmitWhileErrorModalOpen();
    } finally {
      this._autoSubmitAfterErrorInFlight = false;
      if (pendingSummaryStatementAiErrorModal?.dismissed) {
        pendingSummaryStatementAiErrorModal = null;
        return;
      }
      this._restorePendingErrorModalIfNeeded();
      // Stop connect()/Turbo remounts from reopening the modal after submit finishes.
      this._clearPendingErrorModalBridge();
    }
  }

  _siblingSubmitSetNodeKVPairActions() {
    const container = this.getSiblingSubmitButtonContainer();
    if (!container) return [];

    const raw = container.getAttribute('data-case-experience--button-action-on-click-value');
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return normalizeActions(parsed).filter((action) => action.action === 'setNodeKVPair');
    } catch {
      return [];
    }
  }

  async _quietNonAiSubmitWhileErrorModalOpen() {
    const now = Date.now();
    const nodeUid = normalizeNodeUid(this.nodeUidValue);
    const answerCommentUid = this._answerCommentUidFromTemplate();
    const state = {
      [nodeUid]: {
        graded: { value: true, updatedAt: now },
        aiSummaryStatementRequestFeedback: { value: false, updatedAt: now },
        value: { value: this.textareaTarget?.value || '', updatedAt: now },
      },
    };
    if (answerCommentUid) {
      state[normalizeNodeUid(answerCommentUid)] = {
        invisible: { value: false, updatedAt: now },
      };
    }

    // Fold sibling submit unlock actions into this DUS upsert so we avoid apply_state
    // turbo streams that remount the section and reopen the error modal in a loop.
    this._siblingSubmitSetNodeKVPairActions().forEach((action) => {
      const args = action?.arguments;
      if (!args?.uid || args.key == null) return;
      const actionUid = normalizeNodeUid(args.uid);
      if (!state[actionUid]) state[actionUid] = {};
      state[actionUid][args.key] = { value: args.value, updatedAt: now };
    });

    await updateDocumentUserState({
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      body: { document_user_state: { state } },
    });
    this.userSectionStateValue = getDusFromDom() || {};
    this.feedbackStateValue = {
      ...(this.feedbackStateValue || {}),
      graded: { value: true, updatedAt: now },
      aiSummaryStatementRequestFeedback: { value: false, updatedAt: now },
    };

    this.lockSummaryStatementAfterNonAiSubmit({ afterSubmit: false });
    this._notesController()?.handleSubmitClick?.();

    syncAllContinueButtonsForDocument(getDusFromDom() || {});
    dispatchCaseExperienceRefreshContinueButtonsIfTwoCol();
  }

  disableRating() {
    const container = this.aiSummaryFeedbackBlockTarget;
    if (!container) return;
    container.querySelectorAll('.badge-aq-emoticon-input[type="radio"]').forEach((input) => {
      input.disabled = true;
    });
    if (this.hasCommentSubmitTarget) {
      this.commentSubmitTarget.disabled = true;
    }
    const commentInput = container.querySelector('.ai-comment');
    if (commentInput) commentInput.disabled = true;
  }

  _syncAiSummaryRequestFeedbackStateOff() {
    const now = Date.now();
    const nextState = {
      ...(this.feedbackStateValue || {}),
      aiSummaryStatementRequestFeedback: { value: false, updatedAt: now },
    };
    this.feedbackStateValue = nextState;
    const host = this.hasContainerTarget ? this.containerTarget : this.element;
    host.setAttribute(
      'data-case-experience--ai-summary-feedback-feedback-state-value',
      JSON.stringify(nextState),
    );
  }

  _applyOneColumnAiSummaryFallback({ persist = true } = {}) {
    this._syncAiSummaryRequestFeedbackStateOff();
    this._aiFeedbackRequested = false;
    this.clearSubmittingAnswerState();

    if (this.hasAiSummaryFeedbackBlockTarget) {
      const streamId = `ai-summary-feedback-stream-${this.nodeUidValue}`;
      this.aiSummaryFeedbackBlockTarget.innerHTML = `
        <div id="${streamId}" class="ai-summary-feedback-stream-root" data-case-experience--ai-summary-feedback-target="summaryFeedbackStream"></div>
      `;
    }

    if (this.hasTextareaTarget) {
      this.textareaTarget.style.display = '';
      this.textareaTarget.readOnly = false;
    }
    if (this.hasCharCountWrapperTarget) {
      this.charCountWrapperTarget.style.display = '';
    }

    this.setAiFeedbackRequested(false, { persist });

    if (docBodyRequiresAnswerCommentInDom(this.element)) {
      const ac = this.element.parentElement
        ? Array.from(this.element.parentElement.children).find(
            (el) => el.classList && el.classList.contains('answer_comment'),
          )
        : null;
      if (ac) ac.style.removeProperty('display');
    } else {
      removeAnswerCommentFromUI(this.element);
    }

    const siblingButton = this.getSiblingButton();
    if (siblingButton) {
      siblingButton.disabled = !(
        this.textareaTarget.value && this.textareaTarget.value.trim().length > 0
      );
    }
    if (this.hasDisclaimerTarget) {
      this.disclaimerTarget.style.display = 'block';
    }

    const answerControls = this.element.querySelector('.summary-statement-answer-controls');
    if (answerControls) {
      answerControls.style.display = '';
    }
  }

  _recoverFromAiFeedbackError(errorMessage, showErrorModal = true) {
    const deferPersistForModal = showErrorModal;
    if (errorMessage && !deferPersistForModal) {
      void this._persistAiSummaryResponseError(errorMessage);
    }
    if (deferPersistForModal) {
      this._deferredErrorRecoveryErrorMessage = errorMessage || '';
    }
    if (showErrorModal) {
      pendingSummaryStatementAiErrorModal = {
        key: pendingSummaryStatementAiErrorModalKey(this.documentIdValue, this.nodeUidValue),
        documentId: this.documentIdValue,
        nodeUid: normalizeNodeUid(this.nodeUidValue),
        message: errorMessage || '',
        dismissed: false,
      };
      this.showErrorModal();
    }
    this.aiFeedbackState.requested = false;
    this.aiFeedbackState.loading = false;
    this.aiFeedbackState.received = false;
    this.aiFeedbackState.error = errorMessage;
    this.aiFeedbackState.feedback = '';

    this._applyOneColumnAiSummaryFallback({ persist: !deferPersistForModal });
    if (showErrorModal) {
      requestAnimationFrame(() => {
        void this._autoSubmitAfterAiFeedbackError();
      });
    }
  }
}
